import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import {
  fetchTournamentExpenses,
  addTournamentExpense,
  updateTournamentExpenseThunk,
  deleteTournamentExpenseThunk,
} from "../../redux/admin/tournamentExpenses.thunk";
import {
  selectTournamentExpenses,
  selectTournamentExpensesLoading,
} from "../../redux/admin/tournamentExpenses.selector";
import { fetchTournaments } from "../../redux/admin/tournaments.thunk";
import { selectTournamentList } from "../../redux/admin/tournaments.selector";
import { fetchExpenseCategories } from "../../redux/admin/tournamentExpenseCategories.thunk";
import { selectExpenseCategories } from "../../redux/admin/tournamentExpenseCategories.selector";

/* ─── inline SVG icons ─── */
const Icon = ({ d, size = 16, style, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, ...style }}
    {...rest}
  >
    <path d={d} />
  </svg>
);

const PlusIcon     = (p) => <Icon {...p} d="M12 5v14M5 12h14" />;
const EditIcon     = (p) => <Icon {...p} d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />;
const TrashIcon    = (p) => <Icon {...p} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const DownloadIcon = (p) => <Icon {...p} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />;
const SearchIcon   = (p) => <Icon {...p} d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />;
const CloseIcon    = (p) => <Icon {...p} d="M18 6L6 18M6 6l12 12" />;
const FileIcon     = (p) => <Icon {...p} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6" />;
const ChevronIcon  = (p) => <Icon {...p} d="M6 9l6 6 6-6" />;

/* ─── helpers ─── */
const currency = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const invoiceUrl = (file) => {
  if (!file) return "";
  if (file.startsWith("http")) return file;
  return `https://aadhavacademy.com/api/${file}`;
};

const INITIAL_FORM = {
  tournament_id: "",
  expense_category_id: "",
  vendor_name: "",
  vendor_phone: "",
  amount: "",
  remarks: "",
  invoice_file: null,
  remove_invoice: false,
};

/* ─── tokens ─── */
const C = {
  bg:           "#f8fafc",
  surface:      "#ffffff",
  border:       "#e2e8f0",
  borderStrong: "#cbd5e1",
  text:         "#0f172a",
  textSub:      "#475569",
  textMuted:    "#94a3b8",
  accent:       "#2563eb",
  accentBg:     "#eff6ff",
  accentBorder: "#bfdbfe",
  accentText:   "#1d4ed8",
  dangerBg:     "#fef2f2",
  dangerBorder: "#fecaca",
  dangerText:   "#b91c1c",
  successBg:    "#f0fdf4",
  successBorder:"#bbf7d0",
  successText:  "#15803d",
  radius:       "10px",
  radiusPill:   "999px",
  shadow:       "0 1px 4px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.04)",
  shadowModal:  "0 20px 50px rgba(15,23,42,.18)",
};

/* ─── small reusable atoms ─── */
const Pill = ({ children, color = "accent" }) => {
  const palettes = {
    accent:  { bg: C.accentBg,  border: C.accentBorder,  text: C.accentText },
    danger:  { bg: C.dangerBg,  border: C.dangerBorder,  text: C.dangerText },
    success: { bg: C.successBg, border: C.successBorder, text: C.successText },
  };
  const p = palettes[color] || palettes.accent;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: C.radiusPill,
      background: p.bg, border: `1px solid ${p.border}`,
      color: p.text, fontSize: 11, fontWeight: 600,
    }}>
      {children}
    </span>
  );
};

const Field = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: "100%", padding: "11px 13px",
  border: `1px solid ${C.border}`, borderRadius: C.radius,
  fontSize: 14, background: C.surface, color: C.text,
  outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const selectStyle = {
  ...inputStyle,
  appearance: "none", cursor: "pointer",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 13px center",
  paddingRight: 36,
};

const ButtonLoader = ({ color = "#fff" }) => (
  <span className="te-btn-spinner" style={{ borderTopColor: color }} />
);

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
const TournamentExpenses = () => {
  const dispatch = useDispatch();

  const tournaments = useSelector(selectTournamentList);
  const categories  = useSelector(selectExpenseCategories);
  const expenses    = useSelector(selectTournamentExpenses);
  const loading     = useSelector(selectTournamentExpensesLoading);

  const [selectedTournament, setSelectedTournament] = useState("");
  const [search,             setSearch]             = useState("");
  const [showModal,          setShowModal]          = useState(false);
  const [isEditMode,         setIsEditMode]         = useState(false);
  const [editId,             setEditId]             = useState(null);
  const [previewInvoice,     setPreviewInvoice]     = useState("");
  const [form,               setForm]               = useState(INITIAL_FORM);
  const [isMobile,           setIsMobile]           = useState(window.innerWidth < 768);
  
  /* UX Mutation Indicators */
  const [isSubmitting,       setIsSubmitting]       = useState(false);
  const [deletingId,         setDeletingId]         = useState(null);

  /* responsive breakpoint listener */
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  /* data load */
  useEffect(() => {
    dispatch(fetchTournaments());
    dispatch(fetchExpenseCategories());
  }, [dispatch]);

  const loadExpenses = useCallback(() => {
    dispatch(fetchTournamentExpenses({ tournament_id: selectedTournament }));
  }, [dispatch, selectedTournament]);

  useEffect(() => {
    if (selectedTournament) loadExpenses();
  }, [loadExpenses, selectedTournament]);

  /* filter */
  const filteredExpenses = expenses.filter((e) => {
    const t = search.toLowerCase();
    return (
      (e.vendor_name   || "").toLowerCase().includes(t) ||
      (e.category_name || "").toLowerCase().includes(t) ||
      (e.remarks       || "").toLowerCase().includes(t) ||
      String(e.amount  || "").includes(t)
    );
  });

  const totalAmount = filteredExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  /* form handlers */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, invoice_file: file, remove_invoice: false }));
  };

  /* open modals */
  const openAddModal = () => {
    if (!selectedTournament) { toastr.warning("Select a tournament first"); return; }
    setIsEditMode(false); setEditId(null); setPreviewInvoice("");
    setForm({ ...INITIAL_FORM, tournament_id: selectedTournament });
    setShowModal(true);
  };

  const openEditModal = (expense) => {
    setIsEditMode(true); setEditId(expense.expense_id);
    setPreviewInvoice(expense.invoice_file || "");
    setForm({
      tournament_id:       expense.tournament_id,
      expense_category_id: expense.expense_category_id,
      vendor_name:         expense.vendor_name  || "",
      vendor_phone:        expense.vendor_phone || "",
      amount:              expense.amount       || "",
      remarks:             expense.remarks      || "",
      invoice_file:        null,
      remove_invoice:      false,
    });
    setShowModal(true);
  };

  /* delete */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    setDeletingId(id);
    try {
      await dispatch(deleteTournamentExpenseThunk(id)).unwrap();
      toastr.success("Expense deleted");
      loadExpenses();
    } catch (err) { toastr.error(err); }
    finally { setDeletingId(null); }
  };

  /* submit */
  const handleSubmit = async () => {
    if (!form.expense_category_id) { toastr.warning("Select a category"); return; }
    if (!form.amount)               { toastr.warning("Enter an amount");   return; }

    setIsSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== undefined) fd.append(k, v);
    });

    try {
      if (isEditMode) {
        await dispatch(updateTournamentExpenseThunk({ id: editId, payload: fd })).unwrap();
        toastr.success("Expense updated");
      } else {
        await dispatch(addTournamentExpense(fd)).unwrap();
        toastr.success("Expense added");
      }
      setShowModal(false);
      setForm(INITIAL_FORM);
      loadExpenses();
    } catch (err) { toastr.error(err || "Failed"); }
    finally { setIsSubmitting(false); }
  };

  /* ── render ── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .te-wrap * { font-family: 'Inter', sans-serif; box-sizing: border-box; }
        .te-expense-card:hover { border-color: ${C.borderStrong} !important; }
        .te-btn-edit:hover  { background: ${C.bg} !important; }
        .te-btn-del:hover   { background: #fee2e2 !important; }
        .te-add-btn:hover   { background: #1d4ed8 !important; }
        .te-tr:hover td     { background: ${C.bg}; }
        .te-link:hover      { background: #dbeafe !important; }
        .te-input:focus, .te-select:focus, .te-textarea:focus {
          border-color: ${C.accent} !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,.12);
        }
        
        /* MNC Style Spinner definition */
        .te-btn-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          animation: te-spin 0.6s linear infinite;
          flex-shrink: 0;
        }
        @keyframes te-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Skeleton Loading pulse */
        .te-skeleton {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 200% 100%;
          animation: te-pulse 1.4s ease infinite;
          border-radius: 4px;
        }
        @keyframes te-pulse {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @media (max-width: 600px) {
          .te-modal-inner { border-radius: 18px 18px 0 0 !important; }
          .te-grid-2      { grid-template-columns: 1fr !important; }
          .te-table-wrap  { display: none !important; }
          .te-cards-wrap  { display: flex !important; }
          .te-toolbar     { flex-direction: column !important; align-items: stretch !important; }
          .te-search-input{ width: 100% !important; }
          .te-topbar      { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
          .te-metrics     { grid-template-columns: repeat(2, 1fr) !important; }
          .te-modal-overlay{ align-items: flex-end !important; padding: 0 !important; }
        }
      `}</style>

      <div className="te-wrap" style={{ minHeight: "100vh", background: C.bg, padding: isMobile ? 0 : 24, fontFamily: "'Inter', sans-serif" }}>
        <div style={{
          background: C.surface,
          borderRadius: isMobile ? 0 : 16,
          border: isMobile ? "none" : `1px solid ${C.border}`,
          boxShadow: isMobile ? "none" : C.shadow,
          overflow: "hidden",
          minHeight: isMobile ? "100vh" : undefined,
        }}>

          {/* ── TOP BAR ── */}
          <header style={{
            padding: isMobile ? "20px 18px 16px" : "22px 28px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: `1px solid ${C.border}`,
            background: C.surface,
            position: isMobile ? "sticky" : "static",
            top: 0, zIndex: 20, flexWrap: "wrap", gap: 12,
          }} className="te-topbar">
            <div>
              <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 22, fontWeight: 700, color: C.text }}>
                Tournament Expenses
              </h1>
              <p style={{ margin: "3px 0 0", fontSize: 13, color: C.textMuted }}>
                Vendors, invoices &amp; cost tracking
              </p>
            </div>
            <button
              className="te-add-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: isMobile ? "10px 18px" : "10px 18px",
                background: C.accent, color: "#fff",
                border: "none", borderRadius: C.radius,
                fontSize: 14, fontWeight: 600, cursor: "pointer",
                transition: "background .15s",
                width: isMobile ? "100%" : undefined,
                justifyContent: isMobile ? "center" : undefined,
              }}
              onClick={openAddModal}
            >
              <PlusIcon size={15} /> New Expense
            </button>
          </header>

          {/* ── TOOLBAR ── */}
          <div
            className="te-toolbar"
            style={{
              padding: isMobile ? "14px 18px" : "16px 28px",
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 12, flexWrap: "wrap",
              borderBottom: `1px solid ${C.border}`,
              background: "#fafbfc",
            }}
          >
            {/* search */}
            <div style={{ position: "relative", display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
              <SearchIcon size={14} style={{ position: "absolute", left: 12, color: C.textMuted }} />
              <input
                className="te-input te-search-input"
                style={{ ...inputStyle, paddingLeft: 36, width: isMobile ? "100%" : 300 }}
                placeholder="Search vendor, category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* tournament picker */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, width: isMobile ? "100%" : undefined }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub, whiteSpace: "nowrap" }}>Tournament</label>
              <select
                className="te-select"
                style={{ ...selectStyle, minWidth: isMobile ? undefined : 240, flex: 1 }}
                value={selectedTournament}
                onChange={(e) => setSelectedTournament(e.target.value)}
              >
                <option value="">Select tournament</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── METRICS ── */}
          <div
            className="te-metrics"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
              padding: isMobile ? "14px 18px" : "20px 28px",
            }}
          >
            {[
              { label: "Total expenses", value: loading ? "..." : filteredExpenses.length, mono: false },
              { label: "Total amount",   value: loading ? "..." : currency(totalAmount),   mono: true  },
            ].map((m) => (
              <div key={m.label} style={{
                background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 14, padding: "16px 20px", boxShadow: C.shadow,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>
                  {m.label}
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: m.mono ? C.accent : C.text, height: 32, display: "flex", alignItems: "center" }}>
                  {m.value}
                </div>
              </div>
            ))}
          </div>

          {/* ── MOBILE CARD LIST ── */}
          <div
            className="te-cards-wrap"
            style={{ display: isMobile ? "flex" : "none", flexDirection: "column", gap: 10, padding: "0 16px 32px" }}
          >
            {loading ? (
              <>
                <CardSkeleton />
                <CardSkeleton />
              </>
            ) : !selectedTournament ? (
              <EmptyState icon="🏆" title="Select a tournament" sub="Pick a tournament above to see its expenses." />
            ) : filteredExpenses.length === 0 ? (
              <EmptyState icon="🔍" title="No results" sub="Try a different search term or add a new expense." />
            ) : (
              filteredExpenses.map((expense) => (
                <ExpenseCard
                  key={expense.expense_id}
                  expense={expense}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                  deletingId={deletingId}
                />
              ))
            )}
          </div>

          {/* ── DESKTOP TABLE ── */}
          <div className="te-table-wrap" style={{ display: isMobile ? "none" : "block", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead style={{ background: "#fafbfc" }}>
                <tr>
                  {["Category", "Vendor", "Phone", "Amount", "Invoice", "Remarks", ""].map((h, i) => (
                    <th key={i} style={{
                      padding: "13px 20px", textAlign: i === 6 ? "right" : "left",
                      fontSize: 11, fontWeight: 700, color: C.textMuted,
                      textTransform: "uppercase", letterSpacing: ".06em",
                      borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                  </>
                ) : !selectedTournament ? (
                  <TableMsg cols={7} text="Select a tournament to view expenses." />
                ) : filteredExpenses.length === 0 ? (
                  <TableMsg cols={7} text="No expenses found." />
                ) : (
                  filteredExpenses.map((expense) => (
                    <tr key={expense.expense_id} className="te-tr" style={{ transition: "background .1s", opacity: deletingId === expense.expense_id ? 0.6 : 1 }}>
                      <td style={tdStyle}>
                        <Pill>{expense.category_name}</Pill>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{expense.vendor_name || "—"}</td>
                      <td style={{ ...tdStyle, color: C.textSub }}>{expense.vendor_phone || "—"}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: C.text }}>{currency(expense.amount)}</td>
                      <td style={tdStyle}>
                        {expense.invoice_file ? (
                          <a href={invoiceUrl(expense.invoice_file)} target="_blank" rel="noreferrer"
                            className="te-link"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              padding: "5px 10px", borderRadius: C.radius,
                              background: C.accentBg, color: C.accentText,
                              border: `1px solid ${C.accentBorder}`,
                              fontSize: 12, fontWeight: 600, textDecoration: "none", transition: "background .15s",
                            }}
                          >
                            <DownloadIcon size={12} /> View
                          </a>
                        ) : <span style={{ color: C.textMuted }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, color: C.textSub, maxWidth: 200 }}>{expense.remarks || "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                          <ActionBtn 
                            icon={<EditIcon size={12} />} 
                            label="Edit"   
                            className="te-btn-edit" 
                            disabled={deletingId !== null}
                            onClick={() => openEditModal(expense)} 
                          />
                          <ActionBtn 
                            icon={<TrashIcon size={12} />} 
                            label="Delete" 
                            danger 
                            loading={deletingId === expense.expense_id}
                            disabled={deletingId !== null}
                            onClick={() => handleDelete(expense.expense_id)} 
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </div>
      </div>

      {/* ═══ MODAL ═══ */}
      {showModal && (
        <div
          className="te-modal-overlay"
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,.55)",
            backdropFilter: "blur(4px)", display: "flex",
            justifyContent: "center", alignItems: isMobile ? "flex-end" : "center",
            zIndex: 9999, padding: isMobile ? 0 : 20,
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) setShowModal(false); }}
        >
          <div
            className="te-modal-inner"
            style={{
              width: "100%", maxWidth: 680,
              maxHeight: isMobile ? "92vh" : "90vh",
              display: "flex", flexDirection: "column",
              background: C.surface,
              borderRadius: isMobile ? "18px 18px 0 0" : 16,
              boxShadow: C.shadowModal,
              border: `1px solid ${C.border}`,
              overflow: "hidden",
            }}
          >
            {/* drag handle for mobile */}
            {isMobile && (
              <div style={{ width: 36, height: 4, background: C.border, borderRadius: 2, margin: "12px auto 0" }} />
            )}

            {/* modal header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              padding: "20px 24px 16px",
              borderBottom: `1px solid ${C.border}`,
            }}>
              <div>
                <div style={{
                  display: "inline-block", padding: "3px 10px", borderRadius: C.radiusPill,
                  background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                  color: C.accentText, fontSize: 10, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8,
                }}>
                  Tournament Expense
                </div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>
                  {isEditMode ? "Edit Expense" : "New Expense"}
                </h2>
              </div>
              <button
                disabled={isSubmitting}
                onClick={() => setShowModal(false)}
                style={{
                  width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
                  border: `1px solid ${C.border}`, borderRadius: "50%", background: C.bg,
                  cursor: isSubmitting ? "not-allowed" : "pointer", color: C.textSub, opacity: isSubmitting ? 0.5 : 1
                }}
              >
                <CloseIcon size={14} />
              </button>
            </div>

            {/* modal body */}
            <div style={{ padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, background: C.bg, flex: 1 }}>

              <Field label="Tournament">
                <select disabled={isSubmitting} className="te-select" style={selectStyle} name="tournament_id" value={form.tournament_id} onChange={handleChange}>
                  <option value="">Select tournament</option>
                  {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>

              <Field label="Category">
                <select disabled={isSubmitting} className="te-select" style={selectStyle} name="expense_category_id" value={form.expense_category_id} onChange={handleChange}>
                  <option value="">Select category</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.category_name}</option>)}
                </select>
              </Field>

              <div className="te-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 14 }}>
                <Field label="Vendor name">
                  <input disabled={isSubmitting} className="te-input" style={inputStyle} name="vendor_name" value={form.vendor_name} onChange={handleChange} placeholder="Vendor name" />
                </Field>
                <Field label="Phone">
                  <input disabled={isSubmitting} className="te-input" style={inputStyle} name="vendor_phone" value={form.vendor_phone} onChange={handleChange} placeholder="Mobile number" />
                </Field>
              </div>

              <Field label="Amount (₹)">
                <input disabled={isSubmitting} className="te-input" type="number" style={inputStyle} name="amount" value={form.amount} onChange={handleChange} placeholder="0.00" />
              </Field>

              <Field label="Remarks">
                <textarea
                  disabled={isSubmitting}
                  className="te-textarea"
                  rows={3}
                  style={{ ...inputStyle, minHeight: 88, resize: "vertical", lineHeight: 1.5 }}
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  placeholder="Expense description…"
                />
              </Field>

              <Field label="Invoice (image / PDF)">
                <input disabled={isSubmitting} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={handleFileChange} style={{ fontSize: 13, color: C.textSub }} />
              </Field>

              {form.invoice_file && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", background: C.surface,
                  border: `1px solid ${C.border}`, borderRadius: C.radius,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <FileIcon size={18} style={{ color: C.accent }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{form.invoice_file.name}</div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>{(form.invoice_file.size / 1024).toFixed(1)} KB</div>
                    </div>
                  </div>
                  <button
                    disabled={isSubmitting}
                    onClick={() => setForm((p) => ({ ...p, invoice_file: null }))}
                    style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, color: C.dangerText, borderRadius: C.radius, padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  >
                    Remove
                  </button>
                </div>
              )}

              {!form.invoice_file && previewInvoice && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <a href={invoiceUrl(previewInvoice)} target="_blank" rel="noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 12px", borderRadius: C.radius,
                    background: C.accentBg, color: C.accentText, border: `1px solid ${C.accentBorder}`,
                    fontSize: 13, fontWeight: 600, textDecoration: "none",
                  }}>
                    <DownloadIcon size={13} /> View existing invoice
                  </a>
                  <button
                    disabled={isSubmitting}
                    onClick={() => { setPreviewInvoice(""); setForm((p) => ({ ...p, remove_invoice: true })); }}
                    style={{ background: C.dangerBg, border: `1px solid ${C.dangerBorder}`, color: C.dangerText, borderRadius: C.radius, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  >
                    Remove invoice
                  </button>
                </div>
              )}
            </div>

            {/* modal footer */}
            <div style={{
              display: "flex", justifyContent: "flex-end", gap: 10,
              padding: "16px 24px", borderTop: `1px solid ${C.border}`, background: C.surface,
            }}>
              <button
                disabled={isSubmitting}
                onClick={() => setShowModal(false)}
                style={{ padding: "10px 20px", border: `1px solid ${C.border}`, borderRadius: C.radius, background: C.bg, color: C.textSub, fontSize: 14, fontWeight: 600, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.6 : 1 }}
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                onClick={handleSubmit}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", border: "none", borderRadius: C.radius, background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.8 : 1 }}
              >
                {isSubmitting && <ButtonLoader />}
                {isEditMode ? (isSubmitting ? "Updating..." : "Update expense") : (isSubmitting ? "Saving..." : "Save expense")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── sub-components ─── */

const tdStyle = {
  padding: "15px 20px",
  borderBottom: `1px solid #f1f5f9`,
  fontSize: 13,
  color: "#0f172a",
  verticalAlign: "middle",
};

const TableMsg = ({ cols, text }) => (
  <tr>
    <td colSpan={cols} style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8", fontSize: 14 }}>
      {text}
    </td>
  </tr>
);

const ActionBtn = ({ icon, label, onClick, danger, className, loading, disabled }) => (
  <button
    className={className}
    disabled={disabled || loading}
    onClick={onClick}
    style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "6px 10px", borderRadius: 8,
      border: `1px solid ${danger ? "#fecaca" : "#e2e8f0"}`,
      background: danger ? "#fef2f2" : "#fff",
      color: danger ? "#b91c1c" : "#475569",
      fontSize: 12, fontWeight: 600, cursor: (disabled || loading) ? "not-allowed" : "pointer", 
      transition: "background .15s",
      opacity: (disabled && !loading) ? 0.5 : 1
    }}
  >
    {loading ? <ButtonLoader color={danger ? C.dangerText : C.textSub} /> : icon}
    {label}
  </button>
);

const ExpenseCard = ({ expense, onEdit, onDelete, deletingId }) => {
  const isCurrentDeleting = deletingId === expense.expense_id;
  return (
    <div
      className="te-expense-card"
      style={{
        background: "#fff", border: "1px solid #e2e8f0",
        borderRadius: 14, padding: "16px", boxShadow: "0 1px 3px rgba(0,0,0,.05)",
        transition: "border-color .15s",
        opacity: (deletingId && !isCurrentDeleting) ? 0.6 : 1
      }}
    >
      {/* top row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <span style={{
          display: "inline-flex", alignItems: "center",
          padding: "4px 10px", borderRadius: 999,
          background: "#eff6ff", border: "1px solid #bfdbfe",
          color: "#1d4ed8", fontSize: 11, fontWeight: 600,
        }}>
          {expense.category_name}
        </span>
        <span style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
          {currency(expense.amount)}
        </span>
      </div>

      {/* vendor */}
      {expense.vendor_name && (
        <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>
          {expense.vendor_name}
        </div>
      )}
      {expense.vendor_phone && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>{expense.vendor_phone}</div>
      )}
      {expense.remarks && (
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, marginBottom: 12 }}>
          {expense.remarks}
        </div>
      )}

      {/* bottom row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {expense.invoice_file ? (
          <a
            href={invoiceUrl(expense.invoice_file)}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "5px 10px", borderRadius: 8,
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              color: "#15803d", fontSize: 11, fontWeight: 600, textDecoration: "none",
            }}
          >
            <DownloadIcon size={12} /> Invoice
          </a>
        ) : (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>No invoice</span>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          <button
            disabled={deletingId !== null}
            onClick={() => onEdit(expense)}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "6px 10px", borderRadius: 8,
              border: "1px solid #e2e8f0", background: "#fff",
              color: "#475569", fontSize: 12, fontWeight: 600, cursor: deletingId !== null ? "not-allowed" : "pointer",
              opacity: deletingId !== null ? 0.5 : 1
            }}
          >
            <EditIcon size={12} /> Edit
          </button>
          <button
            disabled={deletingId !== null}
            onClick={() => onDelete(expense.expense_id)}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "6px 10px", borderRadius: 8,
              border: `1px solid ${isCurrentDeleting ? "#fecaca" : "#fecaca"}`, 
              background: "#fef2f2",
              color: "#b91c1c", fontSize: 12, fontWeight: 600, cursor: deletingId !== null ? "not-allowed" : "pointer",
            }}
          >
            {isCurrentDeleting ? <ButtonLoader color={C.dangerText} /> : <TrashIcon size={12} />}
            {isCurrentDeleting ? "..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* Modern MNC Shimmer Mock Lines */
const TableSkeleton = () => (
  <tr>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 22, width: 90, borderRadius: 999 }} /></td>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 16, width: 120 }} /></td>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 16, width: 100 }} /></td>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 16, width: 80 }} /></td>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 26, width: 60, borderRadius: 6 }} /></td>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 16, width: 150 }} /></td>
    <td style={tdStyle}><div className="te-skeleton" style={{ height: 28, width: 110, marginLeft: "auto", borderRadius: 8 }} /></td>
  </tr>
);

const CardSkeleton = () => (
  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "16px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
      <div className="te-skeleton" style={{ height: 22, width: 80, borderRadius: 999 }} />
      <div className="te-skeleton" style={{ height: 24, width: 90 }} />
    </div>
    <div className="te-skeleton" style={{ height: 16, width: "60%", marginBottom: 6 }} />
    <div className="te-skeleton" style={{ height: 14, width: "40%", marginBottom: 12 }} />
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div className="te-skeleton" style={{ height: 24, width: 70, borderRadius: 8 }} />
      <div className="te-skeleton" style={{ height: 24, width: 100, borderRadius: 8 }} />
    </div>
  </div>
);

const EmptyState = ({ icon, title, sub }) => (
  <div style={{ textAlign: "center", padding: "52px 24px" }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.5 }}>{sub}</div>
  </div>
);

export default TournamentExpenses;